import React, { useMemo } from 'react';

import styled from 'styled-components';

import {
  DataStatus,
  LatestData,
  NowCommonProps,
  ObsDome,
  ObsId,
  ObsMode,
  ObsPhase,
  ObsStatus,
  ObsWeatherState,
} from 'libs/types';
import { convertDetailTime, convertZeroNumber } from 'libs/utils';
import useDelay from 'libs/hook/useDelay';
import { NOW_DELAY_SECONDS } from 'libs/const';

const MODE_STATUS = {
  Shutdown: 'bad',
  Maintenance: 'ok',
  Initialization: 'warn',
  Automatic: undefined,
  Warning: undefined,
} as const satisfies Record<ObsMode, DataStatus | undefined>;

const STATUS_STATUS = {
  Shutdown: 'bad',
  Failure: 'bad',
  Critical: 'bad',
  Warning: 'ok',
  Preparation: 'warn',
  Recovery: 'warn',
  Initialization: 'warn',
  Initializing: 'warn',
  Normal: undefined,
} as const satisfies Record<ObsStatus, DataStatus | undefined>;

const DOME_STATUS = {
  Opened: 'good',
  Closed: undefined,
  Closing: undefined,
  Opening: undefined,
  'N/A': undefined,
} as const satisfies Record<ObsDome, DataStatus | undefined>;

const PHASE_STATUS = {
  Observing: 'good',
  Standby: undefined,
} as const satisfies Record<ObsPhase, DataStatus | undefined>;

const WEAHTER_STATUS = {
  'N/A': 'bad',
  Bad: 'bad',
  Good: undefined,
} as const satisfies Record<ObsWeatherState, DataStatus | undefined>;

const Wrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  min-width: 1150px;
  line-height: 1.2;

  td,
  th {
    border: 1px solid rgba(160, 160, 160, 0.9);

    &:first-child {
      border-left: 0;
    }
    &:last-child {
      border-right: 0;
    }
  }

  tr:last-child {
    td {
      border-top: 3px double rgba(160, 160, 160, 0.9);
    }
  }
`;

const Tr = styled.tr<{ isDay?: boolean }>`
  background-color: ${(props) => (props.isDay ? '#6B6B6B' : '#474849')};
`;

const Th = styled.th`
  background-color: #7c827d;
  color: #293d45;
  font-size: 16px;
  font-weight: 700;
  padding: 13px 10px;
`;

const Td = styled.td`
  padding: 8px 10px;
  font-size: 15px;
  font-weight: 600;
  text-align: center;
`;

const DataSpan = styled.span<{ status?: DataStatus }>`
  color: ${({ status, theme: { now } }) => {
    if (!status) return '#bdcacd';
    if (status === 'bad') return now.status.bad;
    if (status === 'ok') return now.status.ok;
    if (status === 'good') return now.status.good;
    if (status === 'warn') return now.status.warn;
  }};
`;

const WeatherInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WeatherValue = styled(DataSpan)`
  display: block;
  width: 60px;
`;

interface ObsNameProps {
  updatedAt?: string;
  children: React.ReactNode;
}

const ObsName = ({ updatedAt, children }: ObsNameProps) => {
  const { isDelay } = useDelay({ target: updatedAt, delayTime: NOW_DELAY_SECONDS, obsId: undefined });

  return <DataSpan {...(isDelay && { status: 'ok' })}>{children}</DataSpan>;
};

interface LatestTimeProps {
  updatedAt?: string;
  children: React.ReactNode;
}

const LatestTime = ({ updatedAt, children }: LatestTimeProps) => {
  const { isDelay, diffSeconds } = useDelay({ target: updatedAt, delayTime: NOW_DELAY_SECONDS, obsId: undefined });

  const delay = useMemo(() => {
    if (!isDelay) return undefined;
    const { minutes, hours } = convertDetailTime(diffSeconds);

    return `+ ${convertZeroNumber(hours)}:${convertZeroNumber(minutes)}`;
  }, [isDelay, diffSeconds]);

  return <DataSpan {...(delay && { status: 'ok' })}>{delay ?? children}</DataSpan>;
};

interface ObsInfoTableProps extends NowCommonProps {
  latestData?: Record<ObsId, LatestData>;
}

const ObsInfoTable = ({ obsOrderList, obsInfo, latestData }: ObsInfoTableProps) => {
  return (
    <Wrapper>
      <Table>
        <thead>
          <tr>
            <Th>CI</Th>
            <Th>OBS</Th>
            <Th style={{ minWidth: '120px' }}>LST</Th>
            <Th>MODE</Th>
            <Th>STATUS</Th>
            <Th>DOME</Th>
            <Th>PHASE</Th>
            <Th>WEATHER &#40;T . H . C . W&#41;</Th>
          </tr>
        </thead>
        <tbody>
          {obsOrderList.map((obsId) => {
            const obsName = obsInfo?.[obsId].obs_name;
            const checkin = latestData?.[obsId].checkin;
            const latestTime = latestData?.[obsId].latest_time;
            const mode = latestData?.[obsId].mode;
            const status = latestData?.[obsId].status;
            const dome = latestData?.[obsId].dome;
            const phase = latestData?.[obsId].phase;
            const weatherState = latestData?.[obsId].weather_state;
            const t = latestData?.[obsId].t;
            const h = latestData?.[obsId].h;
            const c = latestData?.[obsId].c;
            const w = latestData?.[obsId].w;

            const updatedAt = latestData?.[obsId].updated_at;

            const latestTimeHour = latestTime ? new Date(latestTime).getHours() : undefined;
            const isDay = latestTimeHour === undefined ? false : latestTimeHour >= 6 && latestTimeHour < 18;

            return (
              <Tr key={`obs_${obsId}_table_row_empty`} isDay={isDay}>
                <Td>
                  <DataSpan>{checkin ? '★' : <>&nbsp;</>}</DataSpan>
                </Td>
                <Td>
                  <ObsName updatedAt={updatedAt}>{obsName ?? <>&nbsp;</>}</ObsName>
                </Td>
                <Td>
                  <LatestTime updatedAt={updatedAt}>{latestTime ?? <>&nbsp;</>}</LatestTime>
                </Td>
                <Td>
                  <DataSpan {...(mode && { status: MODE_STATUS[mode] })}>
                    {mode ? mode === 'Warning' ? 'NORMAL' : mode.toUpperCase() : <>&nbsp;</>}
                  </DataSpan>
                </Td>
                <Td>
                  <DataSpan {...(status && { status: STATUS_STATUS[status] })}>
                    {status ? status === 'Warning' ? 'NORMAL' : status.toUpperCase() : <>&nbsp;</>}
                  </DataSpan>
                </Td>
                <Td>
                  <DataSpan {...(dome && { status: DOME_STATUS[dome] })}>
                    {dome ? dome.toUpperCase() : <>&nbsp;</>}
                  </DataSpan>
                </Td>
                <Td>
                  <DataSpan {...(phase && { status: PHASE_STATUS[phase] })}>
                    {phase ? phase.toUpperCase() : <>&nbsp;</>}
                  </DataSpan>
                </Td>
                <Td>
                  <WeatherInfo>
                    <WeatherValue {...(weatherState && { status: WEAHTER_STATUS[weatherState] })}>
                      {weatherState ? weatherState.toUpperCase() : <>&nbsp;</>}
                    </WeatherValue>
                    <WeatherValue status={t?.status === 'N' ? 'bad' : undefined}>
                      {t?.value || t?.value === 0 ? t.value === 'N/A' ? 'N/A' : `${t.value.toFixed(1)}℃` : <>&nbsp;</>}
                    </WeatherValue>
                    <WeatherValue status={h?.status === 'N' ? 'bad' : undefined}>
                      {h?.value || h?.value === 0 ? h.value === 'N/A' ? 'N/A' : `${h.value.toFixed(1)}%` : <>&nbsp;</>}
                    </WeatherValue>
                    <WeatherValue
                      status={c?.status === 'N' || (typeof c?.value === 'number' && c.value < -180) ? 'bad' : undefined}
                    >
                      {c?.value || c?.value === 0 ? (
                        c.value === 'N/A' ? (
                          'N/A'
                        ) : c.value <= -180 || c.value === 0 ? (
                          0
                        ) : (
                          `${c.value.toFixed(1)}`
                        )
                      ) : (
                        <>&nbsp;</>
                      )}
                    </WeatherValue>
                    <WeatherValue status={w?.status === 'N' ? 'bad' : undefined}>
                      {w?.value || w?.value === 0 ? (
                        w.value === 'N/A' ? (
                          'N/A'
                        ) : (
                          `${w.value.toFixed(1)}m/s`
                        )
                      ) : (
                        <>&nbsp;</>
                      )}
                    </WeatherValue>
                  </WeatherInfo>
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </Table>
    </Wrapper>
  );
};

export default ObsInfoTable;
